import React from "react";
import dayjs from "dayjs";

import WMTSCapabilities from "ol/format/WMTSCapabilities.js";

import { optionsFromCapabilities } from "ol/source/WMTS";

export const YEAR_IN_MINUTES = 60 * 24 * 365;

export function ConvertISOToDate(isoDate) {
    if (isoDate != null) {
        const date = new Date(isoDate);
        const dateOpts = { day: "2-digit", month: "2-digit", year: "numeric" };
        const timeOpts = { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit", timeZoneName: "short" };

        return date.toLocaleDateString("en-US", dateOpts) + " " + date.toLocaleTimeString(navigator.language, timeOpts);
    } else {
        return "";
    }
}

export function GetColorFromState(state, stateStore) {
    let color = "#3E436F"; //Defaults to Dark Blue

    if (stateStore && state) {
        const selectedState = stateStore.find((selectedState) => selectedState.state == state.toUpperCase());
        color = selectedState ? selectedState.color : color;
    }

    return color;
}

export function ConvertCoordinatesToVertices(coords) {
    const vertices = [];
    for (const coord of coords) {
        const vertice = {
            lng: coord[0],
            lat: coord[1]
        };
        vertices.push(vertice);
    }
    return vertices;
}

export function ConvertFeetToMeters(feet) {
    const meters = parseFloat((feet * 0.3048).toFixed(2));
    return meters;
}

export function ConvertMetersToFeet(meters) {
    const feet = parseFloat(Math.round(meters * 3.28084));
    return feet;
}

export function ConvertSpeedToKnots(speed) {
    const knots = parseFloat(Math.round(speed * 1.94384));
    return knots;
}

export function GetVerticalSpeedIndicator(fpm) {
    const indicator = fpm >= 250 ? `↑` : fpm <= -250 ? `↓` : "";
    return indicator;
}

export function IsValidDate(dateString) {
    const time = new Date(dateString).getTime();
    return !!time && !isNaN(time);
}

export function IsInvalidDate(startDate, endDate) {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return !IsValidDate(startDate) || !IsValidDate(endDate) || start > end;
}

export function DateHasAlreadyPassed(dateString) {
    const start = new Date(dateString).getTime();
    const currentTime = new Date().getTime();
    return !IsValidDate(dateString) || start < currentTime;
}

// Checks if the inputted min/max altitudes are invalid.
export function IsInvalidAltitude(minAltitude, maxAltitude) {
    const min = parseFloat(minAltitude);
    const max = parseFloat(maxAltitude);

    if (isNaN(min) || isNaN(max) || min > max || min < 0 || max <= 0) {
        return true;
    }

    return false;
}

export function ConvertNauticalMilesToMeters(nauticalMiles) {
    return nauticalMiles * 1852;
}

export function GetTimezone() {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date();
    const timezoneAbbreviation = Intl.DateTimeFormat(undefined, { timeZone: userTimezone, timeZoneName: "short" })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName").value;

    return timezoneAbbreviation;
}

export function IsValidLaancCode(code) {
    if (code && code.length === 12 && code.startsWith("CAL")) {
        return true;
    } else {
        return false;
    }
}

export function getOperationDateFromIsoString(iso) {
    const currentDate = new Date();
    const targetDate = new Date(iso);

    const timeDifference = Math.abs(targetDate.getTime() - currentDate.getTime());
    const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));

    if (hoursDifference < 24) {
        let hour = targetDate.getHours();
        const minute = targetDate.getMinutes();

        const amOrPm = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12;
        return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${amOrPm}`;
    } else {
        const month = targetDate.getMonth() + 1;
        const day = targetDate.getDate();
        return `${month}/${day}`;
    }
}

export function getConstraintDurationFromInput(input) {
    if (typeof input === "string") {
        if (input === "") {
            return input;
        } else if (/^[0-9]*$/.test(input) && input.length < 8) {
            return input;
        }
    }
    return undefined;
}

export function getFilteredVolumesFromUserInputs(volumes, startDate, durationMin, permanent = false) {
    const duration = durationMin || 60;
    const startTime = startDate && startDate.isValid() ? startDate.valueOf() : new Date().getTime();
    const endTime = dayjs(startTime).add(duration, "minutes").valueOf();
    return volumes.filter((volume) => {
        const volumeStartTime = volume.time_start ? new Date(volume.time_start).getTime() : null;
        const volumeEndTime = volume.time_end ? new Date(volume.time_end).getTime() : null;

        if (!volumeStartTime) return false;

        // permanent logic
        if (permanent && volume.permanent) return true;
        if (permanent) return startTime <= volumeEndTime;
        if (volume.permanent) return endTime >= volumeStartTime;

        // normal bounded overlap
        if (!volumeEndTime) return false;

        return startTime <= volumeEndTime && endTime >= volumeStartTime;
    });
}

export function getSourceIdsFromUserInput(event) {
    if (typeof event.target.value === "string") {
        return event.target.value.trim().replace(/^[,\s]+|[,\s]+$/g, "");
    } else {
        return "";
    }
}

export function FormatUUID(input) {
    input = input.replace(/[^0-9a-fA-F]/gi, "");
    const parts = [input.slice(0, 8), input.slice(8, 12), input.slice(12, 16), input.slice(16, 20), input.slice(20, 32)];
    return parts.filter((p) => p).join("-");
}

export function IsInvalidUUID(uuid) {
    const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    return !uuid || !uuidRegex.test(uuid);
}

export function ConvertLinksToURLs(text, color = "#FFFACD") {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) => {
        if (!part.match(urlRegex)) {
            return part;
        }
        return (
            <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: color, wordBreak: "break-all" }}>
                {part}
            </a>
        );
    });
}

export function ConvertMpsToKnots(mps) {
    if (typeof mps === "number") {
        return Math.round(mps * 1.94384);
    } else {
        return undefined;
    }
}

export function ConvertMpsToFpm(mps) {
    if (typeof mps === "number") {
        return mps * 196.9;
    } else {
        return undefined;
    }
}

export function ConvertMetersToNauticalMiles(meters) {
    const METERS_PER_NAUTICAL_MILE = 1852;
    return meters / METERS_PER_NAUTICAL_MILE;
}

export function getTimePassedFromIso(iso) {
    const currentTimestamp = new Date().getTime();
    const isoTimestamp = new Date(iso).getTime();
    const elapsedMilliseconds = currentTimestamp - isoTimestamp;

    if (elapsedMilliseconds < 60000) {
        const seconds = Math.floor(elapsedMilliseconds / 1000);
        return `${seconds}s ago`;
    } else if (elapsedMilliseconds < 3600000) {
        const minutes = Math.floor(elapsedMilliseconds / 60000);
        return `${minutes} min${minutes !== 1 ? "s" : ""} ago`;
    } else if (elapsedMilliseconds < 86400000) {
        const hours = Math.floor(elapsedMilliseconds / 3600000);
        return `${hours} hr${hours !== 1 ? "s" : ""} ago`;
    } else {
        const days = Math.floor(elapsedMilliseconds / 86400000);
        return `${days} day${days !== 1 ? "s" : ""} ago`;
    }
}

export async function getOptionsFromCapabilities(option) {
    return new Promise((resolve, reject) => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/xml" }
        };
        fetch("/truweather/wmts/getCapabilities", requestOptions)
            .then((response) => {
                return response.ok ? response.text() : Promise.reject(response);
            })
            .then((text) => {
                const parser = new WMTSCapabilities();
                const result = parser.read(text);

                const options = optionsFromCapabilities(result, {
                    layer: option.layer,
                    matrixSet: "EPSG:3857"
                });
                resolve({
                    ...options,
                    tileLoadFunction: (tile) => {
                        const tileCoord = tile.getTileCoord();
                        const z = tileCoord[0];
                        const x = tileCoord[1];
                        const y = tileCoord[2];

                        const injectedTruWeatherUrl = option.url.replace("{TileMatrix}", z).replace("{TileCol}", x).replace("{TileRow}", y);
                        const updatedUrl = encodeURIComponent(injectedTruWeatherUrl);
                        tile.getImage().src = `/truweather/wmts/getTile/${updatedUrl}`;
                    }
                });
            })
            .catch((err) => {
                reject(err);
            });
    });
}

export async function getHasValidTruWeatherCredentials() {
    return new Promise((resolve, reject) => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch("/truweather/wmts/checkCredentials", requestOptions)
            .then((response) => {
                return response.ok ? response.json() : Promise.reject(response);
            })
            .then((hasValidCredentials) => {
                resolve(hasValidCredentials);
            })
            .catch((err) => {
                reject(err);
            });
    });
}
